import {typeboxResolver} from '@hookform/resolvers/typebox'
import {announce} from '@react-aria/live-announcer'
import {type Static, Type} from '@sinclair/typebox'
import {TypeCompiler} from '@sinclair/typebox/compiler'
import {Value} from '@sinclair/typebox/value'
import {Button, FieldError, Form, Input, Label, TextField} from 'react-aria-components'
import {Controller, type SubmitHandler, useForm} from 'react-hook-form'

import Timer from '@/components/Timer/Timer'
import {ContactUsRoute} from '@/routeRegistry'
import {triggerResetPassword} from '@/services/authenticationService'

import style from './ResetPasswordStep1.module.scss'

const schema = Type.Object({
  email: Type.String({
    default: '',
    format: 'email',
    errorMessage: 'Please enter a valid email address',
  }),
})

const typecheck = TypeCompiler.Compile(schema)

type Schema = Static<typeof schema>

export default memo(function ResetPasswordStep1() {
  const [isResetting, setIsResetting] = useState(false)
  const [succeeded, setSucceeded] = useState(false)
  const [linkIsInvalid, setLinkIsInvalid] = useState(false)

  const {handleSubmit, control, setError, getValues} = useForm<Schema>({
    mode: 'onTouched',
    resolver: typeboxResolver(typecheck),
    defaultValues: Value.Create(schema),
  })

  const onSubmit: SubmitHandler<Schema> = data => {
    setIsResetting(true)
    void triggerResetPassword({
      email: data.email,
    })
      .then(result => {
        if (result) {
          if (typeof result === 'string') {
            announce(result, 'assertive')
            setError(
              'email',
              {message: result},
              {
                shouldFocus: true,
              },
            )
          } else {
            setSucceeded(true)
            setLinkIsInvalid(false)
          }
        }
      })
      .finally(() => {
        setIsResetting(false)
      })
  }

  const latestHandleLinkBecameInvalid = useStableCallback(() => {
    setLinkIsInvalid(true)
  })

  return (
    <>
      {!succeeded ? (
        <>
          <div className={style.Helper}>
            Enter your email and click &quot;Send Email Link&quot; to receive an email containing a
            link to reset your account’s password
          </div>
          <Form className={style.Form} onSubmit={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              name='email'
              render={({
                field: {name, value, onChange, onBlur, ref},
                fieldState: {invalid, error},
              }) => (
                <TextField
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  validationBehavior='aria'
                  isInvalid={invalid}
                  isRequired
                  className={style.Field}
                >
                  <Label className={style.Label}>Email</Label>
                  <Input className={style.Input} ref={ref} />
                  {!!error?.message && (
                    <FieldError className={style.Error}>{error.message}</FieldError>
                  )}
                </TextField>
              )}
            />
            <div className={style.SubmitArea}>
              <Button className={style.SignUpBtn} type='submit' isDisabled={isResetting}>
                {!isResetting ? 'Send email link' : 'Sending...'}
              </Button>
            </div>
          </Form>
        </>
      ) : (
        <div className={style.EmailSent}>
          <div role='alert'>
            <p>Check email for reset link</p>
            <p>
              An email has been sent to <strong>{getValues('email')}</strong>. Check your inbox and
              click the reset link provided. If you don't receive an email in a few minutes, please
              check your "spam"/"junk" folder or{' '}
              <Link className={style.Link} to={ContactUsRoute.fullPath}>
                contact us
              </Link>
              .
            </p>
            <p>
              {!linkIsInvalid ? (
                <>
                  Reset link will become invalid in{' '}
                  <span className={style.Timer}>
                    <Timer
                      onDone={latestHandleLinkBecameInvalid}
                      className={style.Timer}
                      seconds={300}
                    />
                  </span>
                </>
              ) : (
                <>Link expired</>
              )}
            </p>
          </div>
          <Button
            className={style.ResendBtn}
            isDisabled={!linkIsInvalid}
            onPress={() => {
              onSubmit({
                email: getValues('email'),
              })
            }}
          >
            {!isResetting ? 'Resend email' : 'Resending...'}
          </Button>
        </div>
      )}
    </>
  )
})
